import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useCloneAgent } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics/analytics';
import { AppRoutes } from '@/utils/types';
import { IAgent } from '@d-id/types';
import { Button, ButtonProps } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ShareTemplateButton } from './ShareTemplateButton';

const HeaderContainer = styled.header`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StyledBackButton = styled(IconButton)`
    padding: 0;
    border-radius: 12px;
    height: 40px;
    color: ${({ theme }) => theme.colors.black[700]};
    margin-right: auto;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const BackText = styled.p`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 500;
    line-height: 160%;
    letter-spacing: 0.15px;
    margin-left: 10px;
    margin-right: 12px;
`;

const UseTemplateButton = styled(Button)<ButtonProps>`
    min-width: 132px;

    // TODO: remove this once we have a proper ui-components publish process
    ${({ loading, theme }) =>
        loading &&
        `
        background-color: ${theme.colors.secondary[500]} !important;

        span {
            color: white !important;
        }
    `}
`;

interface Props {
    template: IAgent;
}

export const Header = ({ template }: Props) => {
    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { mutateAsync: clone, isLoading: isCloning } = useCloneAgent();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    const handleClickBack = () => {
        navigate(AppRoutes.Agent);
    };

    const handleUseTemplate = async () => {
        mixpanel?.track('agent-create', { event: 'click', from: 'agent-template', template: template.id });

        const agent = await clone(template.id);
        navigateToAgentEditor({ agentId: agent.id, mixPanelFrom: 'agent-template-page' });
    };

    return (
        <HeaderContainer>
            <StyledBackButton onClick={handleClickBack}>
                <MaskedIcon src={ArrowLeft} size="32px" />
                <BackText>{template.preview_name}</BackText>
            </StyledBackButton>

            <ShareTemplateButton template={template} />

            <UseTemplateButton color="secondary" onClick={handleUseTemplate} loading={isCloning}>
                Use template
            </UseTemplateButton>
        </HeaderContainer>
    );
};
